import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Target, Users, Calendar, Shield, ArrowDown } from "lucide-react";
import { processData } from "../../../../data/howItWorks/processData";
import "./ProcessFunnel.css";

gsap.registerPlugin(ScrollTrigger);

const iconMap = {
  Target: <Target size={32} strokeWidth={1.5} />,
  Users: <Users size={32} strokeWidth={1.5} />,
  Calendar: <Calendar size={32} strokeWidth={1.5} />,
  Shield: <Shield size={32} strokeWidth={1.5} />,
};

const ProcessFunnel = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const funnelRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );

      const steps = funnelRef.current.querySelectorAll(".funnel-step");
      gsap.fromTo(
        steps,
        { opacity: 0, y: 60, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          stagger: 0.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: funnelRef.current,
            start: "top 75%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );

      const arrows = funnelRef.current.querySelectorAll(".funnel-arrow");
      gsap.fromTo(
        arrows,
        { opacity: 0, y: -20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: funnelRef.current,
            start: "top 75%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="process-funnel" ref={sectionRef}>
      <div className="process-funnel__container">
        <div className="process-funnel__header" ref={titleRef}>
          <h2 className="process-funnel__title">{processData.funnel.title}</h2>
          <p className="process-funnel__subtitle">
            {processData.funnel.subtitle}
          </p>
        </div>

        <div className="process-funnel__visual" ref={funnelRef}>
          {processData.funnel.steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div
                className="funnel-step"
                style={{
                  "--step-color": step.color,
                  "--step-width": `${100 - index * 15}%`,
                }}
              >
                <div className="funnel-step__number">
                  {String(step.id).padStart(2, "0")}
                </div>
                <div className="funnel-step__icon">
                  {iconMap[step.iconName]}
                </div>
                <h3 className="funnel-step__title">{step.title}</h3>
                <p className="funnel-step__description">{step.description}</p>
              </div>

              {index < processData.funnel.steps.length - 1 && (
                <div className="funnel-arrow">
                  <ArrowDown size={32} strokeWidth={2} />
                </div>
              )}
            </React.Fragment>
          ))}

          <div className="funnel-end">
            <div className="funnel-end__icon">💰</div>
            <div className="funnel-end__text">Consistent Revenue</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessFunnel;
